import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { AttachmentButton, IAttachmentButtonProps } from '..';
import { BaseArea, BaseButton, BaseCheckbox, BaseInput, Toggle, Variables } from '../../base';

interface INote {
    isNote: boolean;
}

interface ILockable {
    locked: boolean;
}

export const Controls = styled.div<INote>`
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 16px 12px;
    border-top: 1px solid ${Variables.ThemeLight.Border2};
    background: ${Variables.ThemeLight.Background2};
    position: relative;

    ${(props: INote) => props.isNote && noteControls}
`;

export const ComposerRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 2px;
`;

export const ComposerMain = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const InputWrapper = styled.div`
    display: flex;
    width: 100%;
`;

export const InputContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 8px;
`;

export const InputBox = styled.div<ILockable>`
    display: flex;
    flex-grow: 1;
    align-items: flex-end;
    min-height: 40px;
    background-color: ${Variables.ThemeLight.Background3};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 8px;
    padding: 9px 12px;

    ${(props: ILockable) =>
        props.locked &&
        `
        opacity: 0.7;
    `}

    &:focus-within {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 3px ${Variables.ThemeLight.PrimaryOpacity3};
    }

    & textarea {
        font-size: 14px;
        background: transparent;
    }
`;

export const InputAttachments = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

export const HiddenInputWrapper = styled.div`
    display: none;
`;

export const AttachInput: any = styled(BaseInput)`
    display: none;
`;

export const AttachButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    min-width: 36px;
    width: 36px;
    height: 36px;
    padding: 0;
    background-color: transparent;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: ${Variables.ThemeLight.TextMild};

    &:hover:not(:disabled) {
        background-color: ${Variables.ThemeLight.NeutralMild1};
        color: ${Variables.ThemeLight.TextMedium};
    }
`;

export const AttachIcon = styled(Icon)`
    vertical-align: middle;
    fill: currentColor;
`;

export const StyledAttachmentButton = styled(AttachmentButton)<IAttachmentButtonProps>`
    && {
        min-width: 0;
        max-width: 240px;
        padding: 5px 8px;
        margin-top: 0;
        background: ${Variables.ThemeLight.NeutralMild1};
        border: 1px solid ${Variables.ThemeLight.Border2};
        border-radius: 5px;
    }

    && > div:first-of-type {
        width: 18px;
        height: 18px;
        border-radius: 4px;
    }
`;

export const Textarea = styled(BaseArea)`
    flex-grow: 1;
    font-size: 14px;
    line-height: 1.45;
    color: ${Variables.ThemeLight.TextStrong};
    background-color: transparent;
    border: 0;
    min-height: 40px;
    outline: none;
    resize: none;
    padding: 0;

    &::placeholder {
        color: ${Variables.ThemeLight.TextMild};
    }
`;

export const SendButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    min-width: 36px;
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 8px;
    border: none;
    color: ${Variables.Colors.white};

    svg {
        fill: ${Variables.Colors.white};
        color: ${Variables.Colors.white};
    }

    &:disabled {
        background: ${Variables.ThemeLight.NeutralMild1};
        background-color: ${Variables.ThemeLight.NeutralMild1};
        cursor: default;

        svg {
            fill: ${Variables.ThemeLight.TextMild};
            color: ${Variables.ThemeLight.TextMild};
        }
    }
`;

export const SendButtonWithLabel = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    width: auto;
    height: 40px;
    padding: 0 16px 0 12px;
    border-radius: 4px;
    gap: 6px;

    svg {
        fill: ${Variables.Colors.white};
    }
`;

export const SendButtonLabel = styled.span`
    display: none;
`;

export const SendButtonLabelVisible = styled.span`
    display: inline-block;
`;

export const SendIcon = styled(Icon)`
    vertical-align: middle;
    flex-shrink: 0;
    fill: currentColor;
`;

export const Col = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    width: 100%;
`;

export const NoteCheckbox = styled(BaseCheckbox)`
    input[type='checkbox']:checked + div {
        border: 1px solid ${Variables.ThemeLight.PrimaryConstant6};
        background-color: ${Variables.ThemeLight.PrimaryConstant6};
        color: ${Variables.ThemeLight.PrimaryConstant11};
    }
    & span:last-child {
        color: ${Variables.ThemeLight.TextMedium};
        font-weight: ${Variables.FontWeight.medium};
        font-size: 12px;
    }
`;

export const NoteToggle = styled(Toggle)`
    --toggle-bgcolor-on: ${Variables.Colors.b200};
`;

export const NoteToggleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const NoteLabel = styled.span`
    font-weight: ${Variables.FontWeight.medium};
    font-size: 12px;
    line-height: 100%;
    color: ${Variables.ThemeLight.TextMedium};
`;

const noteControls = `
    ${InputBox} {
        background: linear-gradient(180deg, ${Variables.ThemeLight.NeutralMild3} 0%, ${Variables.ThemeLight.Background3} 100%);
        border-color: ${Variables.Colors.b300_l90};
    }
    ${InputBox} textarea {
        color: ${Variables.Colors.b300_l62};
    }
`;

export const InputError = styled.div`
    color: ${Variables.Colors.warning};
    font-size: 13px;
    fill: ${Variables.Colors.warning};
    display: flex;
    align-items: center;
`;

export const InputErrorIcon = styled(Icon)`
    width: 16px;
    height: 16px;
    vertical-align: middle;
`;

export const InputErrorMessage = styled.div`
    display: inline-block;
    margin-left: 4px;
`;

export const ActionsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
`;

export const ActionsItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const ActionsCol = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export const SupportActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

export const ProviderHint = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    line-height: 1.35;
    color: ${Variables.ThemeLight.TextMild};
`;

export const DragOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${Variables.Colors.nx16};
    color: ${Variables.Colors.b300};
    font-size: 16px;
    pointer-events: none;
    z-index: 2;
    border-radius: 0;
`;

export const QuickAnswersContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`;

export const QuickAnswerBubble = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 5px 10px 4px;
    background-color: ${Variables.ThemeLight.NeutralMild1};
    color: ${Variables.ThemeLight.TextMedium};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 999px;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: ${Variables.ThemeLight.NeutralMild2};
    }
`;
